import { invalidateTypedQueries, useApiClient } from '@/hooks/useApiClient'
import { GraviButton } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { Modal, message } from 'antd'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useCallback, useState } from 'react'
import { useScheduleItems } from '../../Context/ScheduleItems/ScheduleItemsContext'
import { BulkAddTemplateForm } from '../../Forms/BulkAddTemplate/BulkAddTemplateForm'
import type { BulkAddTemplateOutput } from '../../Forms/BulkAddTemplate/Inputs'

dayjs.extend(timezone)

export function AddTemplateModal(props: Parameters<typeof GraviButton>[0]) {
  const [isOpen, setIsOpen] = useState(false)
  const api = useApiClient()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = api.useMutation('post', '/driver/generate_template_schedule', {
    onSuccess: () => setIsOpen(false),
    onError: (err) => err?.detail && typeof err.detail === 'string' && message.error(err.detail),
    onSettled: () => invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview'),
  })

  const { rows } = useScheduleItems()

  const onSubmit = useCallback(
    (v: BulkAddTemplateOutput) => {
      const start = v.startDate.set('day', 0)
      const set = new Set(v.drivers)
      return mutateAsync({
        body: {
          start_date: start.tz('UTC').startOf('day').format('YYYY-MM-DDT00:00:00'),
          weeks_out: Math.floor((v.endDate.diff(start, 'days') + 1) / 7),
          driver_ids: rows.filter((x) => set.has(x.id) && !x.carrier_id).map((x) => x.id),
          carriers: rows
            .filter((x) => set.has(x.id) && x.carrier_id)
            .map((x) => ({
              depot: x.depot ?? '',
              id: x.carrier_id ?? '',
              resource_num: x.carrier_resource_num ?? 0,
            })),
          template_id: v.templateId,
        },
      })
    },
    [mutateAsync, rows]
  )
  return (
    <>
      <GraviButton
        {...props}
        onClick={(e) => {
          setIsOpen(true)
          props?.onClick?.(e)
        }}
      />
      <Modal
        title='Generate Schedules from Template'
        destroyOnClose
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
      >
        <BulkAddTemplateForm
          isPending={isPending}
          onSubmit={onSubmit}
          extraButtons={<GraviButton onClick={() => setIsOpen(false)}>Cancel</GraviButton>}
        />
      </Modal>
    </>
  )
}
